import { cancelOnSessionChange } from '../session-cancellation';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { NGXLogger } from 'ngx-logger';
import { mergeMap, map, catchError, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import { OrganizationsService } from '@api/services/organizations.service';
import * as OrganizationsActions from '../../actions/organizations/specs.actions';
import { MessageService } from 'primeng/api';

@Injectable()
export class OrganizationsSpecsEffects {
  checkSpecAndCreateVersion$;
  updateSpecVersion$;
  deleteSpecVersion$;
  getSpecValidationResults$;
  updateSpecVersionSuccess$;
  updateSpecVersionFailure$;
  deleteSpecVersionSuccess$;
  deleteSpecVersionFailure$;
  reorderSpecs$;
  reorderSpecsSuccess$;
  reorderSpecsFailure$;

  constructor(
    private logger: NGXLogger,
    private actions$: Actions,
    private organizationsService: OrganizationsService,
    private messageService: MessageService
  ) {
      this.checkSpecAndCreateVersion$ = createEffect(() =>
        this.actions$.pipe(
          ofType(OrganizationsActions.checkSpecAndCreateVersion),
          mergeMap(({ organizationId, projectId, uploadIntentId, versionName }) =>
            this.organizationsService.organizationsCheckSpecAndCreateVersion({
              organizationId,
              projectId,
              body: {
                uploadIntentId,
                versionName
              }
            }).pipe(
              map(({ project, results }) => {
                return OrganizationsActions.checkSpecAndCreateVersionSuccess({
                  project: { data: project!, loadedAt: new Date()},
                  uploadIntentId,
                  results: results || []
                });
              }),
              catchError((error) => of(OrganizationsActions.checkSpecAndCreateVersionFailure({ error }))),
            cancelOnSessionChange(this.actions$)
            )
          )
        )
      );
  
      this.updateSpecVersion$ = createEffect(() =>
        this.actions$.pipe(
          ofType(OrganizationsActions.updateSpecVersion),
          mergeMap(({ organizationId, projectId, uploadIntentId, versionName, status }) =>
            this.organizationsService.organizationsUpdateSpecVersion({
              organizationId,
              projectId,
              uploadIntentId,
              body: { versionName, status }
            }).pipe(
              map(({ project, spec }) =>
                OrganizationsActions.updateSpecVersionSuccess({
                  project: { data: project!, loadedAt: new Date() },
                  spec: spec!
                })
              ),
              catchError(error => of(OrganizationsActions.updateSpecVersionFailure({ error }))),
            cancelOnSessionChange(this.actions$)
            )
          )
        )
      );
  
      this.deleteSpecVersion$ = createEffect(() =>
        this.actions$.pipe(
          ofType(OrganizationsActions.deleteSpecVersion),
          mergeMap(({ organizationId, projectId, uploadIntentId }) =>
            this.organizationsService.organizationsDeleteSpecVersion({
              organizationId,
              projectId,
              uploadIntentId
            }).pipe(
              map(({ project, spec }) =>
                OrganizationsActions.deleteSpecVersionSuccess({
                  project: { data: project!, loadedAt: new Date() },
                  spec: spec!
                })
              ),
              catchError(error => of(OrganizationsActions.deleteSpecVersionFailure({ error }))),
            cancelOnSessionChange(this.actions$)
            )
          )
        )
      );
  
      this.getSpecValidationResults$ = createEffect(() =>
        this.actions$.pipe(
          ofType(OrganizationsActions.getSpecValidationResults),
          mergeMap(({ organizationId, projectId, uploadIntentId }) =>
            this.organizationsService.organizationsGetSpecValidationResults({
              organizationId,
              projectId,
              uploadIntentId
            }).pipe(
              map(({ project, results }) =>
                OrganizationsActions.getSpecValidationResultsSuccess({
                  project: { data: project!, loadedAt: new Date() },
                  uploadIntentId,
                  results: results || []
                })
              ),
              catchError(error => of(OrganizationsActions.getSpecValidationResultsFailure({ error }))),
            cancelOnSessionChange(this.actions$)
            )
          )
        )
      );
  
      this.updateSpecVersionSuccess$ = createEffect(() =>
        this.actions$.pipe(
          ofType(OrganizationsActions.updateSpecVersionSuccess),
          tap(({ project }) => {
            this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Spec updated successfully.' });
            this.logger.info('Spec updated successfully', project);
          })
        ),
        { dispatch: false }
      );
  
      this.updateSpecVersionFailure$ = createEffect(() =>
        this.actions$.pipe(
          ofType(OrganizationsActions.updateSpecVersionFailure),
          tap(({ error }) => {
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Update spec failed.' });
            this.logger.error('Update spec version failed', error);
          })
        ),
        { dispatch: false }
      );
  
      this.deleteSpecVersionSuccess$ = createEffect(() =>
        this.actions$.pipe(
          ofType(OrganizationsActions.deleteSpecVersionSuccess),
          tap(({ project }) => {
            this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Spec deleted successfully.' });
            this.logger.info('Spec deleted successfully', project);
          })
        ),
        { dispatch: false }
      );
  
      this.deleteSpecVersionFailure$ = createEffect(() =>
        this.actions$.pipe(
          ofType(OrganizationsActions.deleteSpecVersionFailure),
          tap(({ error }) => {
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Delete spec failed.' });
            this.logger.error('Delete spec version failed', error);
          })
        ),
        { dispatch: false }
      );

      this.reorderSpecs$ = createEffect(() =>
        this.actions$.pipe(
          ofType(OrganizationsActions.reorderSpecs),
          mergeMap(({ organizationId, projectId, order }) =>
            this.organizationsService.organizationsReorderSpecs({
              organizationId,
              projectId,
              body: { order }
            }).pipe(
              map(response =>
                OrganizationsActions.reorderSpecsSuccess({
                  project: { data: response.project!, loadedAt: new Date() }
                })
              ),
              catchError(error => of(OrganizationsActions.reorderSpecsFailure({ error }))),
            cancelOnSessionChange(this.actions$)
            )
          )
        )
      );
      
      this.reorderSpecsSuccess$ = createEffect(() =>
        this.actions$.pipe(
          ofType(OrganizationsActions.reorderSpecsSuccess),
          tap(({ project }) => {
            this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Specs reordered successfully.' });
            this.logger.info('Specs reordered successfully', project);
          })
        ),
        { dispatch: false }
      );
      
      this.reorderSpecsFailure$ = createEffect(() =>
        this.actions$.pipe(
          ofType(OrganizationsActions.reorderSpecsFailure),
          tap(({ error }) => {
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Reorder specs failed.' });
            this.logger.error('Reorder specs failed', error);
          })
        ),
        { dispatch: false }
      );
    }
}